import { Link } from "react-router-dom";
import { Briefcase, Flag, Wrench, TrendingUp, ArrowUpRight } from "lucide-react";
import PageHead from "@/components/PageHead";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const CASES = [
  {
    id: 1,
    kicker: "CASE 01 | WINDOWS SERVER E ACTIVE DIRECTORY",
    title: "Modernização de Infraestrutura e Migração de Active Directory",
    segment: "Ambiente corporativo | Infraestrutura Microsoft",
    challenge:
      "Modernizar um ambiente de servidores que concentrava serviços críticos de autenticação, DNS e DHCP, reduzindo a dependência de um controlador de domínio legado e os riscos associados à infraestrutura existente.",
    solution: [
      "Realizamos o planejamento e a transferência das funções FSMO, reorganização dos serviços de Active Directory, revisão das configurações DNS e DHCP e adequação das políticas de grupo (GPO). A infraestrutura foi reorganizada em ambiente Microsoft Windows Server e Hyper-V, com validações de replicação e integridade dos serviços de domínio.",
    ],
    result: [
      "Funções FSMO transferidas, serviços de domínio validados e infraestrutura de autenticação e rede reorganizada. Os testes de integridade DNS foram concluídos com sucesso, estabelecendo uma base mais consistente para a administração do ambiente.",
    ],
  },
  {
    id: 2,
    kicker: "CASE 02 | RECUPERAÇÃO DE SERVIÇOS CRÍTICOS",
    title: "Recuperação e Estabilização de Ambiente Active Directory",
    segment: "Saúde | Infraestrutura de servidores",
    challenge:
      "Um ambiente com múltiplos controladores de domínio apresentava falhas de replicação do Active Directory, inconsistências de registros DNS e problemas de sincronização SYSVOL, comprometendo a confiabilidade dos serviços de autenticação e administração centralizada.",
    solution: [
      "Executamos diagnóstico dos controladores de domínio, correção das configurações DNS, recuperação da replicação DFSR, reorganização dos serviços SYSVOL e NETLOGON e transferência das funções FSMO para um controlador de domínio atualizado.",
      "Foram realizadas verificações utilizando ferramentas nativas do Windows Server, incluindo DCDIAG e REPADMIN.",
    ],
    result: [
      "Replicação do Active Directory normalizada, registros DNS corrigidos, compartilhamentos SYSVOL e NETLOGON restabelecidos e funções FSMO consolidadas. Os testes finais de DNS e replicação foram concluídos com sucesso.",
    ],
  },
  {
    id: 3,
    kicker: "CASE 03 | MICROSOFT AZURE E LINUX",
    title: "Modernização de Servidores Linux em Microsoft Azure",
    segment: "Plataforma web corporativa | Cloud Computing",
    challenge:
      "Modernizar a infraestrutura de aplicações web hospedadas em Microsoft Azure, substituindo componentes legados e preparando o ambiente para versões atualizadas de sistema operacional, servidor web e PHP.",
    solution: [
      "Implementamos um novo ambiente baseado em Ubuntu Server 24.04, Nginx e PHP-FPM 8.3, realizando a migração dos arquivos e configurações das aplicações WordPress.",
      "O projeto incluiu ajustes de configuração, integração com banco de dados, revisão de redirecionamentos HTTP e implementação de certificados SSL/TLS com Let's Encrypt.",
    ],
    result: [
      "Novo ambiente de homologação implantado e validado, aplicações migradas para uma base tecnológica atualizada e acesso HTTPS configurado. A modernização permitiu preparar a plataforma para as próximas etapas de otimização e dimensionamento de recursos.",
    ],
  },
  {
    id: 4,
    kicker: "CASE 04 | ARMAZENAMENTO CORPORATIVO",
    title: "Migração de Dados e Reorganização de Compartilhamentos Corporativos",
    segment: "Saúde | Servidores de arquivos",
    challenge:
      "Reorganizar a infraestrutura de armazenamento de um servidor de arquivos corporativo, transferindo diretórios e compartilhamentos para um novo volume sem comprometer a estrutura de permissões e o acesso dos usuários.",
    solution: [
      "Planejamos e executamos a migração utilizando Robocopy, preservando permissões NTFS, atributos e estrutura dos diretórios.",
      "Os compartilhamentos SMB foram recriados no volume de destino, mantendo as configurações de acesso. Foram realizados procedimentos de validação e preservação temporária dos dados de origem para contingência.",
    ],
    result: [
      "Mais de 300 GB de dados migrados entre volumes, com compartilhamentos recriados, permissões preservadas e acesso aos arquivos validado. O ambiente passou a utilizar a nova organização de armazenamento, mantendo os dados de origem disponíveis durante a etapa de contingência.",
    ],
  },
  {
    id: 5,
    kicker: "CASE 05 | REDES CORPORATIVAS E VIRTUALIZAÇÃO",
    title: "Diagnóstico e Recuperação de Conectividade em Rede Corporativa",
    segment: "Ambiente corporativo | Redes e Hyper-V",
    challenge:
      "Um ambiente virtualizado apresentou perda de conectividade com serviços de domínio e redes segmentadas após uma alteração física na conexão entre o servidor e o switch. O incidente afetou a comunicação de serviços dependentes da infraestrutura de rede corporativa.",
    solution: [
      "Realizamos investigação da conectividade entre o host Hyper-V, os adaptadores de rede virtuais e o switch gerenciável.",
      "A análise identificou que a alteração de porta havia modificado o comportamento esperado das VLANs. Foi orientado o restabelecimento da conexão física adequada e a validação dos serviços de rede.",
    ],
    result: [
      "Conectividade restabelecida após a correção da conexão física, com normalização do acesso aos serviços de domínio e às redes segmentadas. A causa do incidente foi identificada e documentada, permitindo orientar a prevenção de ocorrências semelhantes.",
    ],
  },
  {
    id: 6,
    kicker: "CASE 06 | BANCO DE DADOS E HOMOLOGAÇÃO",
    title: "Sincronização de Banco de Dados entre Produção e Homologação",
    segment: "Aplicações corporativas | Windows Server e MariaDB",
    challenge:
      "Atualizar um ambiente de homologação com os dados da base de produção, mantendo a separação entre os ambientes e permitindo a realização de testes em uma estrutura consistente.",
    solution: [
      "Executamos a preparação da base de homologação, recriação do banco de dados e importação controlada de um backup SQL da produção.",
      "Foram verificadas as configurações de acesso ao serviço MariaDB, a estrutura dos bancos e a consistência da importação.",
    ],
    result: [
      "Base de homologação atualizada e estrutura validada, com 579 tabelas identificadas tanto no banco de produção quanto no de homologação. O procedimento permitiu disponibilizar uma base sincronizada para as atividades de teste e validação da aplicação.",
    ],
  },
];

const BLOCKS = [
  { key: "Desafio", icon: Flag, field: "challenge", label: "challenge" },
  { key: "Solução", icon: Wrench, field: "solution", label: "solution" },
  { key: "Resultado", icon: TrendingUp, field: "result", label: "result" },
];

export default function Projects() {
  return (
    <div>
      <PageHead
        title="Cases & Projetos de Infraestrutura | IPRS Soluções em TI"
        description="Cases reais de infraestrutura de TI: migração de Active Directory, recuperação de serviços críticos, Azure e Linux, armazenamento, redes e banco de dados."
        path="/projetos"
      />

      <section className="relative pt-28 lg:pt-40 pb-12 overflow-hidden">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="container-x relative">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue">Projetos</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-graphite leading-[1.15] max-w-3xl">
              Projetos reais e arquiteturas implementadas
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base lg:text-lg text-steel leading-relaxed max-w-2xl">
              Cada projeto é apresentado em três frentes: o desafio técnico, a solução
              arquitetada e o resultado obtido.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container-x space-y-6">
          {CASES.map((c) => (
            <Reveal key={c.id} delay={0.03}>
              <article
                data-testid={`project-card-${c.id}`}
                className="border border-line rounded-xl overflow-hidden bg-white"
              >
                <div className="bg-mist border-b border-line px-6 sm:px-10 py-6 flex flex-col sm:flex-row sm:items-center gap-3">
                  <span className="font-mono text-xs text-techblue">0{c.id}</span>
                  <div className="flex-1">
                    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-steel">{c.kicker}</p>
                    <h2 className="mt-1.5 text-lg lg:text-xl font-semibold text-graphite leading-snug">{c.title}</h2>
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-steel">
                      <Briefcase className="w-3.5 h-3.5 text-techblue" />
                      {c.segment}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3">
                  {BLOCKS.map((b, i) => (
                    <div
                      key={b.key}
                      data-testid={`project-${b.label}-${c.id}`}
                      className={`px-6 sm:px-10 py-7 ${i < BLOCKS.length - 1 ? "lg:border-r border-line" : ""} border-b lg:border-b-0 border-line`}
                    >
                      <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-techblue">
                        <b.icon className="w-4 h-4" />
                        {b.key}
                      </p>
                      <div className="mt-3 space-y-3">
                        {(Array.isArray(c[b.field]) ? c[b.field] : [c[b.field]]).map((par, idx) => (
                          <p key={idx} className="text-sm text-steel leading-relaxed">
                            {par}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-16 sm:pb-20">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-pale border border-line rounded-xl px-6 sm:px-8 py-6">
              <p className="text-sm text-graphite leading-relaxed flex-1">
                Sua empresa enfrenta um desafio parecido? Converse com nossos especialistas
                sobre o cenário da sua infraestrutura.
              </p>
              <a
                href="/contato"
                data-testid="projects-contact-link"
                className="inline-flex items-center gap-1.5 bg-techblue hover:bg-techblue-hover text-white text-sm font-semibold px-6 py-3 rounded-md transition-colors duration-200 shrink-0"
              >
                Descrever meu cenário
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
